import { useEffect, useMemo, useState } from 'preact/hooks';
import { BlockRenderer } from './BlockRenderer.js';
import { CheckoutFlowRunner } from './CheckoutFlowRunner.js';
import { ConfirmPanel } from './ConfirmPanel.js';
import { ContextualProcessingBand } from './ContextualProcessingBand.js';
import { Lookup } from './Lookup.js';
import { MethodTiles } from './MethodTiles.js';
import { ReturnPage } from './ReturnPage.js';
import { TrustBadges } from './TrustBadges.js';
import { Welcome } from './Welcome.js';
import { type CheckoutConfigDto, type CheckoutSessionDto, loadCheckoutConfig, pickLocale } from './api.js';
import { startDomRecorder, stopDomRecorder } from './dom-recorder.js';
import { pickDefaultMethod } from './method-defaults.js';
import { reconcileSelectedMethod } from './method-reconcile.js';
import { filterMethodsByCurrency } from './method-currency-filter.js';
import { applyMappings } from './prefill.js';
import { replayClient } from './replay-client.js';
import { type RiskSessionResponse, openRiskSession } from './risk-collect.js';
import { resolveRoute } from './route.js';

/**
 * Top-level router for the hosted checkout SPA (bd bj9).
 *
 * Routing is the only thing this component does. The destinations:
 *   - `/`                     → the generic {@link Welcome} landing. Loads no
 *                               tenant, makes no API call.
 *   - `/return/<outcome>`     → the post-HPP {@link ReturnPage}.
 *   - explicit tenant+strategy → the {@link CheckoutApp}, which owns all the
 *                               config-fetch + checkout state.
 *   - anything else           → a loud "no checkout specified" notice. There
 *                               is deliberately NO hardcoded tenant fallback.
 *
 * `CheckoutApp` is a separate component so its data-fetching hooks only ever
 * mount on the checkout route — `/` never touches the network.
 */
export function App() {
  const route = useMemo(() => {
    const navLocale = navigator.language ? navigator.language.slice(0, 2) : 'en';
    return resolveRoute(window.location.pathname, new URLSearchParams(window.location.search), navLocale);
  }, []);

  switch (route.kind) {
    case 'welcome':
      return <Welcome />;
    case 'return':
      return <ReturnPage outcome={route.outcome} />;
    case 'checkout':
      return <CheckoutApp tenant={route.tenant} strategy={route.strategy} locale={route.locale} />;
    case 'checkout-link':
      return <CheckoutApp link={route.token} locale={route.locale} />;
    case 'missing-target':
      return (
        <div class="w-full max-w-lg sm:max-w-xl md:max-w-3xl lg:max-w-5xl p-4 sm:p-6">
          <div class="p-4 border-l-4 border-error bg-error-container/40 text-on-error-container rounded-r">
            <p class="text-h3 font-semibold">No checkout specified.</p>
            <p class="text-body-sm mt-2 text-on-surface-variant">
              This URL is missing a tenant and strategy. A checkout must be opened under an explicit{' '}
              <code>?tenant=…&strategy=…</code> route.
            </p>
          </div>
        </div>
      );
  }
}

/**
 * The hosted checkout shell.
 *
 * Every visible string, color, and trust badge comes from the tenant's
 * CheckoutStrategy.display block — operators edit it in the admin dashboard
 * and the next shopper page-load reflects the change. There is no hardcoded
 * tenant text in this component on purpose.
 */
export function CheckoutApp({
  tenant,
  strategy,
  link,
  locale,
}: {
  tenant?: string;
  strategy?: string;
  /** Opaque link token (the `/c/<token>` path). Tenant resolved server-side. */
  link?: string;
  locale: string;
}) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const [config, setConfig] = useState<CheckoutConfigDto | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [session, setSession] = useState<CheckoutSessionDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [risk, setRisk] = useState<RiskSessionResponse | null>(null);
  const [riskError, setRiskError] = useState<string | null>(null);
  // Currency the flow set (via the set-currency node), surfaced by the runner.
  const [flowCurrency, setFlowCurrency] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadCheckoutConfig(link ? { link } : { tenant: tenant ?? '', strategy: strategy ?? '' })
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        // Pre-select the cheapest enabled tile so the shopper isn't stuck
        // staring at a disabled Verify-and-continue. They can change it
        // before clicking; pre-selection is a default, not a constraint.
        setSelectedMethod((prev) => prev ?? pickDefaultMethod(c.strategy.methodEligibility));
      })
      .catch((e: Error) => {
        if (!cancelled) setConfigError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant, strategy, link]);

  // Re-validate the selected method whenever the flow-set currency resolves:
  // if the current selection is now currency-excluded, fall back to the best
  // currency-valid default (bd currency-aware-payment-methods).
  useEffect(() => {
    if (!config) return;
    const active = flowCurrency ?? session?.amount.currency ?? undefined;
    const visible = filterMethodsByCurrency(
      config.strategy.methodEligibility,
      config.strategy.methodCurrencies,
      active,
    );
    // bd ogc — extracted to method-reconcile so the "keep when still visible,
    // else default" rule is testable in isolation.
    setSelectedMethod((prev) => reconcileSelectedMethod(prev, visible));
  }, [flowCurrency, config, session]);

  // Open the risk session — and both recorders — as soon as the config has
  // loaded. We used to wait until the Lookup form succeeded, which meant
  // every interaction on the Lookup step (typing the reference, picking a
  // method, expanding "More ways to pay") went uncaptured. Starting on
  // first paint means the full checkout journey is on record. The deps array
  // is deliberately narrowed to (tenant, strategy, config?.tenant.id) so the
  // risk session re-opens only on a tenant/strategy change. Unchanged from pre-bj9.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional narrow deps (see above)
  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await openRiskSession({ tenant: config.tenant.id });
        if (cancelled) return;
        setRisk(r);
        if (r.token && r.session_id && r.decision !== 'BLOCK') {
          replayClient.start({ sessionId: r.session_id, token: r.token });
          void startDomRecorder({
            sessionId: r.session_id,
            token: r.token,
            replay: config.tenant.replay,
          });
          if (r.possible_duplicate) {
            replayClient.mark('duplicate.warning_shown', {
              matched_on_key: r.possible_duplicate.matched_on_key,
              delta_seconds: r.possible_duplicate.delta_seconds,
            });
          }
        }
      } catch (e) {
        if (!cancelled) setRiskError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      void replayClient.stop('unmount');
      void stopDomRecorder();
    };
    // Re-run only when the tenant + strategy change — not on every render.
  }, [tenant, strategy, config?.tenant.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The brand color, when set, drives a CSS custom prop the rest of the SPA
  // can read — so a tenant changing `branding.primaryColor` in admin
  // immediately re-tints buttons + accent text on the next page-load.
  const brandStyle = config
    ? ({
        ['--upg-brand' as string]: config.tenant.branding.primaryColor || '#000',
      } as Record<string, string>)
    : undefined;

  if (configError) {
    return (
      <div class="w-full max-w-lg sm:max-w-xl md:max-w-3xl lg:max-w-5xl p-4 sm:p-6">
        <div class="p-4 border-l-4 border-error bg-error-container/40 text-on-error-container rounded-r">
          <p class="text-h3 font-semibold">We couldn't load this checkout.</p>
          <p class="text-body-sm mt-2">{configError}</p>
          <p class="text-body-sm mt-2 text-on-surface-variant">
            {link ? (
              <>
                Checkout link <code>{link}</code>.
              </>
            ) : (
              <>
                Tenant <code>{tenant}</code> · strategy <code>{strategy}</code>.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div class="w-full max-w-lg sm:max-w-xl md:max-w-3xl lg:max-w-5xl p-4 sm:p-6 text-on-surface-variant">
        <p class="text-body-sm">Loading checkout…</p>
      </div>
    );
  }

  // Effective tenant/strategy come from the loaded config — authoritative for
  // both the legacy ?tenant&strategy path and the opaque /c/<token> path (where
  // the props are undefined and the tenant is only known after resolution).
  const effTenant = config.tenant.id;
  const effStrategy = config.strategy.name;

  const d = config.strategy.display;
  const eyebrow = pickLocale(d.eyebrow, locale);
  const title = pickLocale(d.title, locale);
  const subtitle = pickLocale(d.subtitle, locale);
  const refHelp = pickLocale(d.referenceHelpText, locale);
  const submitLabel = pickLocale(d.submitLabel, locale) || 'Continue';
  const payLabel = pickLocale(d.payLabel, locale) || 'Pay securely';
  const payFootnote = pickLocale(d.payFootnote, locale);
  const footerBrand = pickLocale(d.footerBrand, locale);

  // Auto-fill the Lookup form from URL query params per the operator-defined
  // mappings. Empty / missing mappings → no pre-fill. Resolved once per
  // strategy load so the seeds don't change on every render.
  const prefill = applyMappings(config.strategy.queryParamMappings, params);

  // Anchor block slots — rendered into the BlockRenderer wherever the operator
  // dropped a `lookup-form` / `method-tiles` / `consents` / `summary` / `pay-button`
  // block. Pre-session: only lookup + method-tiles are interactive.
  // Post-session (confirmed): the form is replaced with the confirm panel,
  // which itself wraps the consents + pay-button slots internally.
  //
  // When the strategy has an operator-authored `checkoutFlow`, the runner
  // OWNS the lookup-form slot pre-session — it walks the graph and renders
  // whichever page node is current. MethodTiles + consents + summary still
  // render in their own slots, so the visual shell stays identical to the
  // legacy path.
  // Payment-method tiles filtered by the active (flow-set) currency. Before the
  // flow sets a currency this is a no-op (all provider-fulfillable tiles show).
  const activeCurrency = flowCurrency ?? session?.amount.currency ?? undefined;
  const visibleMethods = filterMethodsByCurrency(
    config.strategy.methodEligibility,
    config.strategy.methodCurrencies,
    activeCurrency,
  );

  const flowActive = !session && !!config.strategy.checkoutFlow;
  const slots = session
    ? {
        'lookup-form': (
          <ConfirmPanel
            session={session}
            payLabel={payLabel}
            payFootnote={payFootnote}
            paymentMethod={selectedMethod}
            risk={risk}
            riskError={riskError}
            tenant={effTenant}
            onError={setError}
          />
        ),
      }
    : {
        'lookup-form': flowActive ? (
          <CheckoutFlowRunner
            config={config}
            // biome-ignore lint/style/noNonNullAssertion: guarded by `flowActive` (!!config.strategy.checkoutFlow) above; unchanged from pre-bj9
            flow={config.strategy.checkoutFlow!}
            tenant={effTenant}
            strategy={effStrategy}
            locale={locale}
            selectedMethod={selectedMethod}
            risk={risk}
            riskError={riskError}
            referenceHelpText={refHelp}
            prefill={prefill}
            onSessionReady={(s) => {
              setError(null);
              setSession(s);
            }}
            onCurrency={setFlowCurrency}
          />
        ) : (
          <Lookup
            tenant={effTenant}
            strategy={effStrategy}
            submitLabel={submitLabel}
            referenceHelpText={refHelp}
            prefill={prefill}
            methodSelected={selectedMethod !== null}
            onValidated={(s) => {
              setError(null);
              setSession(s);
            }}
            onError={setError}
          />
        ),
        'method-tiles': (
          <div class="space-y-3">
            <MethodTiles
              methods={visibleMethods}
              incentives={config.strategy.incentives}
              locale={locale}
              selected={selectedMethod}
              onSelect={setSelectedMethod}
            />
            {config.strategy.contextualProcessing?.enabled && (
              <ContextualProcessingBand
                config={config.strategy.contextualProcessing}
                methods={visibleMethods}
                selected={selectedMethod}
                locale={locale}
              />
            )}
          </div>
        ),
      };

  // Operator-authored blocks. If none, fall back to a minimal default sequence
  // so checkout still renders for pre-blocks strategies.
  const blocks =
    config.strategy.blocks && config.strategy.blocks.length > 0
      ? config.strategy.blocks
      : [{ id: 'blk-default-lookup', type: 'lookup-form' as const }];

  return (
    <div
      // Progressive responsive shell:
      //   base    → single column, narrow (mobile-first)
      //   sm 640  → wider single column
      //   md 768  → 2-col layout: form + trust badges sidebar (220px)
      //   lg 1024 → wider container, roomier sidebar (260px), bigger gap
      //   xl 1280 → fully-flexible content column, ~300px sidebar
      // The shell stays centred; padding lives on the page chrome in index.html.
      class="w-full max-w-lg sm:max-w-xl md:max-w-3xl md:grid md:grid-cols-[1fr_220px] md:gap-6 lg:max-w-5xl lg:grid-cols-[1fr_260px] lg:gap-8 xl:max-w-6xl xl:grid-cols-[1fr_300px] items-start"
      style={brandStyle}
    >
      <div class="space-y-4 sm:space-y-5 lg:space-y-6">
        <div>
          {eyebrow && <p class="upg-eyebrow text-on-primary-container">{eyebrow}</p>}
          {/* Type ramps up at lg+ — the hero line was too quiet on a 27-inch monitor. */}
          <h1 class="text-h1 lg:text-[clamp(1.875rem,2.4vw,2.5rem)] font-semibold tracking-tight leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p class="text-body-sm lg:text-body-md text-on-surface-variant mt-1 lg:mt-2 max-w-prose">
              {subtitle}
            </p>
          )}
        </div>
        <BlockRenderer blocks={blocks} locale={locale} slots={slots} />
        {/* Operator-authored `checkoutFlow` (Scope B) is wired in via the
            `lookup-form` slot above when no session exists — the runner walks
            the graph while MethodTiles + consents + other anchor blocks still
            render in their slots. Once the runner reaches `redirect_hpp` it
            calls `onSessionReady` and the slot switches to ConfirmPanel. */}
        {error && (
          <div class="p-3 border-l-4 border-error bg-error-container/50 text-on-error-container rounded-r">
            <p class="text-body-sm">{error}</p>
          </div>
        )}
        {footerBrand && (
          <p class="text-[10px] text-on-surface-variant text-center pt-2">
            {footerBrand} · {new Date().getFullYear()}
          </p>
        )}
        {config.tenant.branding.supportEmail && (
          <p class="text-[10px] text-on-surface-variant text-center">
            Need help?{' '}
            <a class="underline" href={`mailto:${config.tenant.branding.supportEmail}`}>
              {config.tenant.branding.supportEmail}
            </a>
          </p>
        )}
      </div>
      <TrustBadges items={d.trustBadges} locale={locale} />
    </div>
  );
}
